import React, { useState, useContext, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { UsuarioContext } from "../../../../../providers/userContext";
import { getChildrenAaffs } from "../../../../../services/aaffs.service";
import { loginAsAAFF } from "../../../../../services/auth.service";
import DataTable from "../../../../../components/Tables/DataTable";
import FilterDropdown from "../../../../../components/filtros/FilterDropdown";
import Empty from "../../../../../icons/empty.png";
import { Search, X, ChevronDown, ChevronRight, LogIn } from "lucide-react";
import provinces from "@doncicuto/es-provinces";
import { IconButton } from "@mui/material";

const buildHierarchy = (flatData) => {
  const map = {};
  const roots = [];
  flatData.forEach(item => { map[item.id] = { ...item, children: [] }; });
  flatData.forEach(item => {
    if (item.parent_id && map[item.parent_id]) {
      map[item.parent_id].children.push(map[item.id]);
    } else {
      roots.push(map[item.id]);
    }
  });
  return roots;
};

const flattenHierarchy = (hierarchy, expandedRows) => {
  const result = [];
  const flatten = (items) => {
    items.forEach(item => {
      result.push(item);
      if (item.children?.length > 0 && expandedRows[item.id]) flatten(item.children);
    });
  };
  flatten(hierarchy);
  return result;
};

function OficinasLegalia() {
  const { t } = useTranslation(['aaff', 'common']);
  const user = useContext(UsuarioContext);
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [busqueda, setBusqueda] = useState({});
  const [busquedaActiva, setBusquedaActiva] = useState(false);
  const [clearFilterActivo, setClearFilterActivo] = useState(false);
  const [sortField, setSortField] = useState("razonSocial");
  const [sortOrder, setSortOrder] = useState("ASC");
  const hasInitialFetch = useRef(false);

  const fetchData = useCallback(async ({ orderBy, order } = {}) => {
    if (orderBy) {
      setSortField(orderBy);
      setSortOrder(order || "ASC");
      return;
    }
    if (!user?.id) return;
    try {
      setLoading(true);
      const response = await getChildrenAaffs(user.id, {
        filtros: {},
        params: { page: 1, limit: 10000, order: "ASC", orderBy: "razonSocial" },
      });
      setRawData((response.data || []).map(row => ({
        id: row.id,
        razonSocial: row.razonSocial,
        nif: row.nif,
        parent_id: row.parent_id,
        provincia: row.provincia || "-",
        nivel: row.nivel || 1,
      })));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && !hasInitialFetch.current) {
      hasInitialFetch.current = true;
      fetchData();
    }
  }, [user?.id]);

  const toggleRow = useCallback((id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const hierarchy = useMemo(() => buildHierarchy(rawData), [rawData]);
  const displayRows = useMemo(() => flattenHierarchy(hierarchy, expandedRows), [hierarchy, expandedRows]);

  const filteredRows = useMemo(() => {
    const filtered = displayRows.filter(row => {
      const matchNombre = !busqueda.buscadorGeneral || row.razonSocial.toLowerCase().includes(busqueda.buscadorGeneral.toLowerCase());
      const matchProvincia = !busqueda.provincia || row.provincia === busqueda.provincia;
      return matchNombre && matchProvincia;
    });
    return [...filtered].sort((a, b) => {
      const valA = (a[sortField] || "").toString().toLowerCase();
      const valB = (b[sortField] || "").toString().toLowerCase();
      const cmp = valA.localeCompare(valB);
      return sortOrder === "ASC" ? cmp : -cmp;
    });
  }, [displayRows, busqueda, sortField, sortOrder]);

  const sortedProvinces = useMemo(() =>
    [...provinces].sort((a, b) => a.name.localeCompare(b.name)),
    []
  );

  const handleChange = (name, value) => {
    const updated = { ...busqueda, [name]: value || undefined };
    for (const key in updated) {
      if (!updated[key]) delete updated[key];
    }
    setBusqueda(updated);
  };

  const oficinasRows = [
    {
      id: "razonSocial",
      label: t('fields.companyName', { defaultValue: 'Razón Social' }),
      overrideFunc: (data, row) => {
        const hasChildren = row.children?.length > 0;
        const isExpanded = expandedRows[row.id];
        const nivel = row.nivel || 1;
        return (
          <div className="flex items-center gap-1" style={{ paddingLeft: `${(nivel - 1) * 24}px` }}>
            {hasChildren ? (
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleRow(row.id); }} style={{ padding: '2px' }}>
                {isExpanded ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronRight size={16} className="text-gray-500" />}
              </IconButton>
            ) : (
              <></>
            )
            }
            <span className="font-medium text-gray-600">{data}</span>
          </div>
        );
      }
    },
    {
      id: "provincia",
      label: t('common:form.province', { defaultValue: 'Provincia' }),
    },
    {
      id: "login",
      label: "",
      noObjeto: true,
      align: "right",
      overrideFunc: (data, row) => (
        <div className="flex justify-end">
          <button
            onClick={() => loginAsAAFF(row.nif)}
            className="flex items-center gap-1.5 px-6 py-1.5 text-xs font-medium rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
            style={{ border: '1px solid #e5e7eb' }}
            title={t('common:messages.loginAs', { name: row.razonSocial, defaultValue: `Entrar como ${row.razonSocial}` })}
          >
            <LogIn size={14} />
            {t('common:actions.enter', { defaultValue: 'Entrar' })}
          </button>
        </div>
      )
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <div className="max-w-8xl mx-auto bg-white shadow-sm p-3 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-5">
          {t('officesList', { defaultValue: 'Listado de oficinas' })}
        </h1>

        {/* Filtros estilo Legalia */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
          <FilterDropdown
            label={t('common:form.province', { defaultValue: 'Provincia' })}
            value={busqueda.provincia || ""}
            options={[
              { value: "", label: t('common:filters.all', { defaultValue: 'Todos' }) },
              ...sortedProvinces.map(p => ({ value: p.name, label: p.name })),
            ]}
            onChange={(v) => handleChange("provincia", v)}
          />

          {Object.keys(busqueda).length > 0 && (
            <button
              onClick={() => setBusqueda({})}
              className="flex items-center gap-1 py-1.5 px-3 text-sm rounded-lg text-red-500 hover:bg-red-50 cursor-pointer"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <X className="w-3.5 h-3.5" />
              {t('common:actions.clear', { defaultValue: 'Limpiar' })}
            </button>
          )}

          <div className="hidden sm:flex flex-1" />

          <div className="relative flex items-center w-full sm:w-auto">
            <Search className="absolute left-3 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder={t('common:actions.search', { defaultValue: 'Buscar' }) + "..."}
              value={busqueda.buscadorGeneral || ""}
              onChange={(e) => handleChange("buscadorGeneral", e.target.value)}
              className="pl-9 pr-4 py-1.5 text-sm rounded-lg bg-white text-gray-700 w-full sm:w-52 focus:outline-none focus:ring-2 focus:ring-blue-100"
              style={{ border: '1px solid #e5e7eb' }}
            />
          </div>
        </div>

        <DataTable
          variant="legalia"
          data={filteredRows}
          meta={{ total: filteredRows.length, last_page: 1, per_page: filteredRows.length }}
          headers={oficinasRows}
          isLoading={loading}
          fetcher={fetchData}
          filtros={busqueda}
          busquedaActiva={busquedaActiva}
          setBusquedaActiva={setBusquedaActiva}
          clearFilterActivo={clearFilterActivo}
          setClearFilterActivo={setClearFilterActivo}
          mensaje={
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <img src={Empty} alt="Empty" style={{ width: 75 }} />
              <p className="text-base font-semibold text-gray-900">{t('messages.noOffices', { defaultValue: 'No hay oficinas' })}</p>
            </div>
          }
        />
      </div>
    </div>
  );
}

export default OficinasLegalia;
